import _ from "lodash";
import React from "react";
import styled from "styled-components";
import { withTranslation, WithTranslation } from "react-i18next";

import images from "src/images";
import { colors } from "src/styles/colors";
import { media } from "src/utils/media";
import {
  Body2Pre,
  Body2A,
  Body2,
  CaptionPre,
  Caption,
} from "src/components/Typography";
import { translate } from "src/locales";
import { ActionType, CategoryType, gTagEvent } from "src/configs/analytics";

type Props = WithTranslation;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-width: 1440px;
  background-color: ${colors.bg.black.main};
  padding-top: 120px;
  padding-bottom: 320px;
  ${media.mobile`
    min-width: auto;
    flex-direction: column;
    padding-top: 49px;
    padding-bottom: 113px;
  `}
`;

const Inner = styled.div`
  display: flex;
  flex-direction: row;
  width: 1352px;
  ${media.mobile`
    flex-direction: column;
    width: 100%;
    padding-left: 24px;
    padding-right: 24px;
  `}
`;

const LeftGroup = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  ${media.mobile`
    flex: 0;
  `}
`;

const InnerLeftGroup = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const PoweredBy = styled(Caption)`
  display: flex;
  flex-direction: column;
  margin-top: 40px;
  ${media.mobile`
    margin-top: 20px;
  `}
`;

const PoweredByText = styled.div`
  color: ${colors.text.light.body};
`;

const HaechiLogo = styled.img`
  width: 109.9px;
  height: 16px;
  margin-top: 12px;
  cursor: pointer;
  ${media.mobile`
    margin-top: 8px;
  `}
`;

const RightGroup = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-end;
  ${media.mobile`
    flex-direction: column;
    justify-content: flex-start;
    margin-top: 56px;
  `}
`;

const Logo = styled.img`
  width: 107px;
  height: 22px;
  align-self: flex-start;
  ${media.mobile`
    width: 77px;
    height: 16px;
  `}
`;

const Description = styled(CaptionPre)`
  color: ${colors.text.light.body};
  margin-top: 28px;
  ${media.mobile`
    width: 100%;
    margin-top: 20px;
  `}
`;

const CompanyName = styled(Caption)`
  color: ${colors.text.light.body};
  margin-top: 16px;
  ${media.mobile`
    margin-top: 12px;
  `}
`;

const Link = styled(Body2A)`
  display: flex;
  flex-direction: row;
  align-items: center;
  color: ${colors.text.light.body};
  cursor: pointer;
`;

const EmailLink = styled(Link)`
  margin-right: 48px;
`;

const MediumLink = styled(Link)`
  ${media.mobile`
    margin-top: 8px;
  `}
`;

const LinkIcon = styled.img`
  width: 26px;
  height: 26px;
  margin-right: 8px;
  ${media.mobile`
    width: 20px;
    height: 20px;
  `}
`;

const Footer = (props: Props) => {
  const { t, i18n } = props;
  const isKo = i18n.language === "ko";

  const onMedium = () => {
    gTagEvent({
      category: CategoryType.Footer,
      action: ActionType.Medium,
    });
    if (isKo) {
      window.open("https://medium.com/henesis");
      return;
    }
    window.open("https://medium.com/henesis-wallet");
  };

  const onHaechi = () => {
    gTagEvent({
      category: CategoryType.Footer,
      action: ActionType.HaechiLabs,
    });
    window.open("https://haechi.io/");
  };

  const onEmail = () => {
    gTagEvent({
      category: CategoryType.Footer,
      action: ActionType.Email,
    });
  };

  return (
    <Container>
      <Inner>
        <LeftGroup>
          <InnerLeftGroup>
            <Logo
              src={images.henesisWhiteLogoSrc}
              srcSet={images.henesisWhiteLogo}
            />
            <Description>{translate(["footer", "description"], t)}</Description>
            <CompanyName>© HAECHI LABS Co., Ltd.</CompanyName>
          </InnerLeftGroup>
          <PoweredBy>
            <PoweredByText>powered by</PoweredByText>
            <HaechiLogo
              src={images.haechiWhiteLogoSrc}
              srcSet={images.haechiWhiteLogo}
              onClick={onHaechi}
            />
          </PoweredBy>
        </LeftGroup>
        <RightGroup>
          <EmailLink
            target="_blank"
            href="mailto:hello@henesis.io"
            onClick={onEmail}
          >
            <LinkIcon src={images.emailLogoSrc} srcSet={images.emailLogo} />
            hello@henesis.io
          </EmailLink>
          <MediumLink onClick={onMedium}>
            <LinkIcon src={images.mediumLogoSrc} srcSet={images.mediumLogo} />
            {isKo ? "henesis" : "henesis-wallet"}
          </MediumLink>
        </RightGroup>
      </Inner>
    </Container>
  );
};

export default withTranslation()(Footer);
